import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Modal } from "react-bootstrap";
import {
  FiMessageCircle,
  FiSend,
  FiX,
} from "react-icons/fi";

const PAGE_SIZE = 10;

const parseTimeLabel = (comment) => {
  if (comment?.time_ago) return comment.time_ago;
  const raw = comment?.created_at || comment?.createdAt;
  if (!raw) return "just now";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "just now";
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const normalizeComment = (raw) => {
  const userId = raw?.user_id || raw?.user?.id || raw?.commenter_id || raw?.commenter?.id;
  return {
    id: raw?.id,
    body: raw?.body || raw?.comment || "",
    parent_id: raw?.parent_id || raw?.parentId || null,
    user: raw?.user || raw?.commenter || {
      id: userId,
      name: raw?.user_name || "Member",
      profile_image: raw?.profile_image || raw?.avatar || null,
    },
    user_id: userId,
    created_at: raw?.created_at || raw?.createdAt,
    time_ago: raw?.time_ago || raw?.timeAgo,
    can_edit:
      typeof raw?.can_edit === "boolean"
        ? raw.can_edit
        : String(userId) === String(localStorage.getItem("user_id")),
  };
};

const buildCommentTree = (items) => {
  const map = new Map();
  const roots = [];

  items.forEach((item) => {
    map.set(String(item.id), { ...item, replies: [] });
  });

  map.forEach((item) => {
    if (item.parent_id && map.has(String(item.parent_id))) {
      map.get(String(item.parent_id)).replies.push(item);
    } else {
      roots.push(item);
    }
  });

  const sortByCreated = (a, b) => {
    const da = new Date(a.created_at || 0).getTime();
    const db = new Date(b.created_at || 0).getTime();
    return da - db;
  };

  const sortRecursive = (nodes) => {
    nodes.sort(sortByCreated);
    nodes.forEach((node) => sortRecursive(node.replies));
  };

  sortRecursive(roots);
  return roots;
};

const CommentModal = ({ show, onHide, postId }) => {
  const token = localStorage.getItem("token");
  const currentUserId = localStorage.getItem("user_id");

  // Since we might not have the user's avatar readily available, we can grab it from local storage or set a default.
  // Ideally, user profile image is in context or local storage.
  const currentUserAvatar = localStorage.getItem("user_profile_image") || "assets/images/profile/dp.png";

  const [comments, setComments] = useState([]);
  const [composer, setComposer] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [editCommentId, setEditCommentId] = useState(null);
  const [editBody, setEditBody] = useState("");

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const inputRef = useRef(null);

  const tree = useMemo(() => buildCommentTree(comments), [comments]);

  const requestWithFallback = async (requests) => {
    for (let i = 0; i < requests.length; i += 1) {
      try {
        const response = await requests[i]();
        return response;
      } catch (err) {
        const status = err?.response?.status;
        if (status && status !== 404 && status !== 405) {
          throw err;
        }
      }
    }
    throw new Error("No supported comment API route matched.");
  };

  const fetchComments = async (reset = false) => {
    if (!postId || !token) return;

    const nextPage = reset ? 1 : page;

    if (reset) {
      setLoading(true);
      setComments([]);
    } else {
      setLoadingMore(true);
    }

    try {
      const response = await requestWithFallback([
        () =>
          axios.get(`${process.env.REACT_APP_MAIN_SITE_URL}/posts/${postId}/comments`, {
            params: { page: nextPage, per_page: PAGE_SIZE },
            headers: { Authorization: `Bearer ${token}` },
          }),
        () =>
          axios.get(`${process.env.REACT_APP_MAIN_SITE_URL}/post/${postId}/comments`, {
            params: { page: nextPage, per_page: PAGE_SIZE },
            headers: { Authorization: `Bearer ${token}` },
          }),
      ]);

      const payload = response?.data?.data || response?.data;
      const rows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.comments?.data)
        ? payload.comments.data
        : Array.isArray(payload?.comments)
        ? payload.comments
        : [];

      const normalized = rows.map(normalizeComment).filter((item) => item.id);

      setComments((prev) => {
        if (reset) return normalized;
        const map = new Map();
        [...prev, ...normalized].forEach((item) => map.set(String(item.id), item));
        return Array.from(map.values());
      });

      const nextPageUrl = payload?.next_page_url || payload?.comments?.next_page_url;
      setHasMore(Boolean(nextPageUrl) || normalized.length >= PAGE_SIZE);
      setPage((prev) => (reset ? 2 : prev + 1));
    } catch (err) {
      console.error("Failed to fetch comments", err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Fetch comments when modal opens
  useEffect(() => {
    if (show && postId) {
      fetchComments(true);
      setComposer("");
      setReplyTo(null);
      setEditCommentId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, postId, token]);

  const handleReplyClick = (comment) => {
    setReplyTo(comment);
    setEditCommentId(null);
    inputRef.current?.focus();
  };

  const handleSubmitComment = async (event) => {
    event.preventDefault();
    const body = composer.trim();
    if (!body || submitting) return;

    setSubmitting(true);
    try {
      const response = await requestWithFallback([
        () =>
          axios.post(
            `${process.env.REACT_APP_MAIN_SITE_URL}/comments`,
            {
              post_id: postId,
              body,
              parent_id: replyTo?.id || null,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          ),
        () =>
          axios.post(
            `${process.env.REACT_APP_MAIN_SITE_URL}/comment/store`,
            {
              post_id: postId,
              body,
              parent_id: replyTo?.id || null,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          ),
        () =>
          axios.post(
            `${process.env.REACT_APP_MAIN_SITE_URL}/posts/${postId}/comments`,
            {
              body,
              parent_id: replyTo?.id || null,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          ),
      ]);

      const raw = response?.data?.data || response?.data?.comment || response?.data;
      if (raw?.id) {
        const newComment = normalizeComment(raw);
        setComments((prev) => [...prev, newComment]);
      } else {
        fetchComments(true);
      }

      setComposer("");
      setReplyTo(null);
    } catch (err) {
      console.error("Failed to create comment", err);
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (comment) => {
    setEditCommentId(comment.id);
    setEditBody(comment.body || "");
  };

  const cancelEdit = () => {
    setEditCommentId(null);
    setEditBody("");
  };

  const saveEdit = async (commentId) => {
    const body = editBody.trim();
    if (!body) return;

    try {
      await requestWithFallback([
        () =>
          axios.put(
            `${process.env.REACT_APP_MAIN_SITE_URL}/comment/update/${commentId}`,
            { body },
            { headers: { Authorization: `Bearer ${token}` } }
          ),
        () =>
          axios.patch(
            `${process.env.REACT_APP_MAIN_SITE_URL}/comment/update/${commentId}`,
            { body },
            { headers: { Authorization: `Bearer ${token}` } }
          ),
        () =>
          axios.post(
            `${process.env.REACT_APP_MAIN_SITE_URL}/comment/update/${commentId}`,
            { _method: "PUT", body },
            { headers: { Authorization: `Bearer ${token}` } }
          ),
      ]);

      setComments((prev) =>
        prev.map((item) =>
          String(item.id) === String(commentId)
            ? {
                ...item,
                body,
              }
            : item
        )
      );
      cancelEdit();
    } catch (err) {
      console.error("Failed to update comment", err);
    }
  };

const removeComment = async (commentId) => {
  try {
    // Only use DELETE, as that is what your route expects
    await axios.delete(`${process.env.REACT_APP_MAIN_SITE_URL}/comment/delete/${commentId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    setComments((prev) => prev.filter((item) => String(item.id) !== String(commentId)));
  } catch (err) {
    console.error("Failed to delete comment", err);
  }
};

  const renderNode = (comment, depth = 0) => {
    const isOwner =
      comment?.can_edit || String(comment?.user_id || comment?.user?.id) === String(currentUserId);

    return (
      <div className={`fb-comment-row ${depth > 0 ? "reply" : ""}`} key={comment.id}>
        <div className="fb-comment-avatar">
          {comment?.user?.profile_image ? (
            <img src={comment.user.profile_image} alt={comment?.user?.name || "member"} />
          ) : (
            (comment?.user?.name || "M").slice(0, 1).toUpperCase()
          )}
        </div>

        <div className="fb-comment-bubble-wrap">
          <div className="fb-comment-bubble">
            <strong className="fb-comment-name">{comment?.user?.name || "Member"}</strong>

            {editCommentId === comment.id ? (
              <div className="fb-comment-edit-wrap mt-1">
                <input
                  value={editBody}
                  onChange={(e) => setEditBody(e.target.value)}
                  className="fb-comment-edit-input"
                  autoFocus
                />
                <div className="fb-comment-edit-actions mt-1">
                  <button type="button" onClick={() => saveEdit(comment.id)} className="text-primary btn btn-sm btn-link p-0 me-2 text-decoration-none">
                    Save
                  </button>
                  <button type="button" onClick={cancelEdit} className="text-secondary btn btn-sm btn-link p-0 text-decoration-none">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <span className="fb-comment-text">{comment.body}</span>
            )}
          </div>

          <div className="fb-comment-actions">
            <span>{parseTimeLabel(comment)}</span>
            <button type="button" onClick={() => handleReplyClick(comment)}>
              Reply
            </button>
            {isOwner ? (
              <>
                <button type="button" onClick={() => startEdit(comment)}>
                  Edit
                </button>
                <button type="button" onClick={() => removeComment(comment.id)} className="danger">
                  Delete
                </button>
              </>
            ) : null}
          </div>

          {comment.replies?.length > 0 && (
             <div className="fb-comment-replies">
               {comment.replies.map((child) => renderNode(child, depth + 1))}
             </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Modal show={show} onHide={onHide} centered scrollable dialogClassName="fb-comment-modal">
      <Modal.Header className="border-bottom pb-2 position-relative" style={{ borderBottomColor: 'rgba(255,255,255,0.1)' }}>
        <Modal.Title className="w-100 text-center fw-bold fs-5 text-white">Comments</Modal.Title>
        <button type="button" onClick={onHide} className="fb-modal-close-btn position-absolute" style={{ right: '16px', top: '16px' }}>
          <FiX size={24} />
        </button>
      </Modal.Header>

      <Modal.Body className="fb-modal-body">
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-secondary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : null}
        
        {!loading && tree.length === 0 ? (
          <div className="fb-empty-state">
            <div className="fb-empty-icon">
               <FiMessageCircle size={40} color="rgba(255,255,255,0.5)" />
            </div>
            <h5>No comments yet</h5>
            <p>Be the first to comment on this post.</p>
          </div>
        ) : null}

        <div className="fb-comments-list">
          {tree.map((root) => renderNode(root, 0))}

          {!loading && hasMore ? (
            <div className="text-center mt-2 mb-2">
               <button
                  type="button"
                  className="fb-view-more"
                  onClick={() => fetchComments(false)}
                  disabled={loadingMore}
                >
                  {loadingMore ? "Loading..." : "View previous comments"}
               </button>
            </div>
          ) : null}
        </div>
      </Modal.Body>

      <Modal.Footer className="fb-modal-footer">
        {replyTo ? (
          <div className="fb-replying-badge w-100 mb-2">
            Replying to <strong>{replyTo?.user?.name}</strong>
            <button type="button" onClick={() => setReplyTo(null)}>
              <FiX />
            </button>
          </div>
        ) : null}

        <form className="fb-comment-form w-100 d-flex align-items-center" onSubmit={handleSubmitComment}>
          <div className="fb-current-user-avatar me-2">
             <img src={currentUserAvatar} alt="Current User" onError={(e) => {e.target.src = "assets/images/profile/dp.png"}} />
          </div>
          <div className="fb-input-wrapper flex-grow-1 position-relative">
            <input
              ref={inputRef}
              type="text"
              value={composer}
              onChange={(e) => setComposer(e.target.value)}
              placeholder="Write a comment..."
              maxLength={500}
              className="fb-comment-input w-100"
            />
            <button 
              type="submit" 
              className="fb-send-btn position-absolute" 
              disabled={submitting || !composer.trim()}
            >
              <FiSend size={18} />
            </button>
          </div>
        </form>
      </Modal.Footer>

      <style jsx>{`
        .fb-comment-modal .modal-content {
          border-radius: 12px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          max-height: 90vh;
          background-color: #2e1452; /* Dark purple to match theme */
          color: #ffffff;
        }

        .fb-modal-body {
          padding: 16px;
          background-color: transparent;
        }

        .fb-empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          text-align: center;
          color: rgba(255, 255, 255, 0.6);
        }

        .fb-empty-icon {
          margin-bottom: 12px;
        }

        .fb-empty-state h5 {
          font-weight: 700;
          font-size: 1.1rem;
          margin-bottom: 4px;
          color: #ffffff;
        }

        .fb-empty-state p {
          font-size: 0.9rem;
          margin: 0;
        }

        .fb-comments-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .fb-comment-row {
          display: flex;
          align-items: flex-start;
          gap: 8px;
        }

        .fb-comment-row.reply {
          margin-top: 8px;
        }

        .fb-comment-avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          overflow: hidden;
          flex-shrink: 0;
          background: rgba(255, 255, 255, 0.1);
          color: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.8rem;
          font-weight: 600;
        }

        .fb-comment-avatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .fb-comment-bubble-wrap {
          flex: 1;
          min-width: 0;
        }

        .fb-comment-bubble {
          background-color: rgba(255, 255, 255, 0.08);
          border-radius: 18px;
          padding: 8px 12px;
          display: inline-block;
          max-width: 100%;
        }

        .fb-comment-name {
          display: block;
          font-size: 0.85rem;
          font-weight: 600;
          color: #ffffff;
          line-height: 1.2;
          margin-bottom: 2px;
        }

        .fb-comment-text {
          font-size: 0.9375rem;
          color: #ffffff;
          line-height: 1.3333;
          word-break: break-word;
        }

        .fb-comment-actions {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-top: 2px;
          margin-left: 12px;
        }

        .fb-comment-actions span,
        .fb-comment-actions button {
          font-size: 0.75rem;
          color: rgba(255, 255, 255, 0.6);
          font-weight: 600;
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;
        }

        .fb-comment-actions button:hover {
          text-decoration: underline;
          color: #ffffff;
        }

        .fb-comment-actions button.danger {
          color: #df314d;
        }

        .fb-comment-replies {
          margin-top: 8px;
        }

        .fb-modal-footer {
          background-color: transparent;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          padding: 12px 16px;
          display: flex;
          flex-direction: column;
        }

        .fb-current-user-avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          overflow: hidden;
          flex-shrink: 0;
          background: rgba(255, 255, 255, 0.1);
        }

        .fb-current-user-avatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .fb-input-wrapper {
          display: flex;
          align-items: center;
        }

        .fb-comment-input {
          background-color: rgba(255, 255, 255, 0.1);
          border: none;
          border-radius: 20px;
          padding: 8px 40px 8px 16px;
          font-size: 0.9375rem;
          color: #ffffff !important;
          outline: none;
        }

        .fb-comment-input::placeholder {
          color: rgba(255, 255, 255, 0.5);
        }

        .fb-send-btn {
          right: 8px;
          background: none;
          border: none;
          color: #df314d;
          padding: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        }

        .fb-send-btn:disabled {
          color: rgba(255, 255, 255, 0.3);
          cursor: default;
        }

        .fb-replying-badge {
          background-color: rgba(255, 255, 255, 0.1);
          color: #ffffff;
          border-radius: 8px;
          padding: 6px 12px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 0.85rem;
        }

        .fb-replying-badge button {
          background: none;
          border: none;
          color: rgba(255, 255, 255, 0.6);
          cursor: pointer;
          padding: 0;
          display: flex;
        }

        .fb-replying-badge button:hover {
          color: #ffffff;
        }
        
        .fb-view-more {
          background: none;
          border: none;
          color: rgba(255, 255, 255, 0.6);
          font-weight: 600;
          font-size: 0.9rem;
        }
        .fb-view-more:hover {
          text-decoration: underline;
          color: #ffffff;
        }
        
        .fb-comment-edit-input {
            width: 100%;
            background-color: rgba(255, 255, 255, 0.1);
            border: 1px solid rgba(255, 255, 255, 0.2);
            color: #ffffff !important;
            border-radius: 12px;
            padding: 4px 8px;
            font-size: 0.9rem;
            outline: none;
        }

        .fb-comment-modal .modal-header {
            border-bottom: 1px solid rgba(255,255,255,0.1) !important;
        }

        .fb-modal-close-btn {
            background: none;
            border: none;
            color: rgba(255, 255, 255, 0.6);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 4px;
            cursor: pointer;
            transition: color 0.2s;
        }

        .fb-modal-close-btn:hover {
            color: #ffffff;
        }
      `}</style>
    </Modal>
  );
};

export default CommentModal;
